import { useState, useCallback } from 'react';
import { validateEmail, validateRequired, validateTextMinLength } from '../utils/validation';

/**
 * Custom hook for validating networking pass form fields (Simplified - No Pass Codes)
 * @param {Object} formData - The form data to validate
 * @param {Array} allPasses - All existing passes (simple attendee objects) to check for conflicts
 * @returns {Object} Validation state and methods
 */
export const usePassValidation = (formData, allPasses) => {
  const [errors, setErrors] = useState({});

  /**
   * Validate a single field
   */
  const validateField = useCallback((fieldName, value) => {
    let validation = { isValid: true, error: '' };

    switch (fieldName) {
      case 'firstName':
        validation = validateRequired(value, 'First name');
        if (validation.isValid) {
          validation = validateTextMinLength(value, 1, 'First name');
        }
        break;

      case 'lastName':
        validation = validateRequired(value, 'Last name');
        if (validation.isValid) {
          validation = validateTextMinLength(value, 1, 'Last name');
        }
        break;

      case 'designation':
        validation = validateRequired(value, 'Designation');
        if (validation.isValid) {
          validation = validateTextMinLength(value, 2, 'Designation');
        }
        break;

      case 'company':
        validation = validateRequired(value, 'Company');
        break;

      case 'email':
        validation = validateRequired(value, 'Email');
        if (validation.isValid) {
          validation = validateEmail(value);
        }
        if (validation.isValid) {
          // Check for cross-pass conflict
          validation = checkEmailConflict(value);
        }
        break;

      case 'phone':
        validation = validateRequired(value, 'Phone number');
        if (validation.isValid && value.length !== 10) {
          validation = { isValid: false, error: 'Phone number must be exactly 10 digits' };
        }
        if (validation.isValid) {
          // Check for cross-pass conflict
          validation = checkPhoneConflict(value);
        }
        break;

      default:
        break;
    }

    return validation;
  }, [allPasses, formData]);

  /**
   * Check if email conflicts with another pass
   */
  const checkEmailConflict = useCallback((email) => {
    if (!email || !allPasses || !Array.isArray(allPasses)) return { isValid: true, error: '' };

    const conflict = allPasses.find(pass => {
      if (!pass?.email) return false;
      return pass.email.toLowerCase() === email.toLowerCase();
    });

    if (conflict) {
      return {
        isValid: false,
        error: `This email is already used by another pass. Each attendee must have a unique email address.`
      };
    }

    return { isValid: true, error: '' };
  }, [allPasses]);

  /**
   * Check if phone conflicts with another pass
   */
  const checkPhoneConflict = useCallback((phone) => {
    if (!phone || !allPasses || !Array.isArray(allPasses) || !formData.phone?.countryCode) {
      return { isValid: true, error: '' };
    }

    // Strip all non-digits for comparison (defensive - removes any '+', spaces, etc.)
    const currentPhone = `${formData.phone.countryCode}${phone}`.replace(/\D/g, '');

    const conflict = allPasses.find(pass => {
      if (!pass?.phone?.number) return false;
      const existingPhone = `${pass.phone.countryCode || ''}${pass.phone.number}`.replace(/\D/g, '');
      return existingPhone === currentPhone;
    });

    if (conflict) {
      return {
        isValid: false,
        error: `This phone number is already used by another pass. Each attendee must have a unique phone number.`
      };
    }

    return { isValid: true, error: '' };
  }, [allPasses, formData.phone?.countryCode]);

  /**
   * Validate a single field and update errors
   */
  const validateAndSetError = useCallback((fieldName, value) => {
    const validation = validateField(fieldName, value);

    setErrors(prev => ({
      ...prev,
      [fieldName]: validation.error
    }));

    return validation.isValid;
  }, [validateField]);

  /**
   * Validate all fields
   */
  const validateAll = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    // Validate all required fields
    const fields = ['firstName', 'lastName', 'designation', 'company', 'email', 'phone'];

    fields.forEach(field => {
      const value = field === 'phone' ? formData.phone?.number : formData[field];
      const validation = validateField(field, value);

      if (!validation.isValid) {
        newErrors[field] = validation.error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [formData, validateField]);

  /**
   * Clear errors
   */
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Clear error for a specific field
   */
  const clearFieldError = useCallback((fieldName) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  return {
    errors,
    validateField,
    validateAndSetError,
    validateAll,
    clearErrors,
    clearFieldError
  };
};
